import { useDispatch } from 'react-redux';
import React, { FC } from 'react';

import ArchivalAlert from 'src/presentation/components/pages/customAttributes/ArchivalAlert';
import { Body, Row, Section } from 'src/presentation/components/forms/Layout';
import { Button } from 'src/presentation/components/agent_desktop_components';
import customAttributesResource from 'src/actions/customAttributes/customAttributesResource';
import Form, { useForm, FormEditorProps } from 'src/presentation/components/forms/Form';
import Footer from 'src/presentation/components/forms/Footer';
import generateId from 'src/lib/generateId';
import { H1 } from '@ad-scripts/presentation/components/common/headers';
import Input from 'src/presentation/components/forms/Input';
import Switch from 'src/presentation/components/forms/Switch';

const dataAidPrefix = 'admin-custom-attributes-';

const CustomAttributesEditor: FC<FormEditorProps> = ({ errors, item, root }) => {
  const { add, update } = customAttributesResource.effects;
  const { resetErrors } = customAttributesResource.actions;
  const saveHandler = useOnSave();

  const formContext = useForm({
    add,
    defaultItem: { deactivated: false, label: '', key: '' },
    errors,
    item,
    resetErrors,
    root,
    update,
    onSave: saveHandler,
  });

  const { onClose, onSave, isNew } = formContext;

  const keyDescription = isNew
    ? 'This is the name we will use in Webhooks and APIs. It must be unique and is not editable after creation. Usually the key is similar to the Display name'
    : 'Keys cannot be edited. Archive this freeform topic and create a new one if this Key is no longer relevant.';

  return (
    <Form context={formContext}>
      <H1>{isNew ? 'Add' : 'Update'} Freeform Topic</H1>
      {item?.deactivated ? <ArchivalAlert archivedAt={item.updatedAt} /> : null}
      <Body data-aid={`${dataAidPrefix}editor`}>
        <Section>
          <Row>
            <Input
              disabled={isNew ? false : item.deactivated}
              data-aid={`${dataAidPrefix}editor-name-input-displayName`}
              attr="label"
              autoFocus
              label="Display Name"
              placeholder="Enter name"
              description="This is the name that will be displayed in Gladly. It must be unique."
              maxLength={50}
            />
            <Switch data-aid={`${dataAidPrefix}editor-disabled-toggle`} attr="deactivated" label="Active" flipValue />
          </Row>
          <Row>
            <Input
              disabled={!isNew}
              data-aid={`${dataAidPrefix}editor-name-input-key`}
              attr="key"
              label="Key"
              placeholder="Enter key"
              description={keyDescription}
              maxLength={50}
            />
          </Row>
        </Section>
      </Body>

      <Footer>
        <Button data-aid={`${dataAidPrefix}editor-cancel`} type={Button.Types.TEXT} onClick={onClose}>
          Cancel
        </Button>
        <Button data-aid={`${dataAidPrefix}editor-save`} onClick={onSave}>
          Save
        </Button>
      </Footer>
    </Form>
  );
};

function useOnSave() {
  const dispatch = useDispatch();
  return ({ add, isNew, item, resetErrors, update, workingStateRef }) => {
    dispatch(resetErrors());

    const currentWorkingState = workingStateRef.current;
    if (!dispatch(customAttributesResource.effects.validateItem(workingStateRef.current))) return;

    const getAddedorUpdatedMessage = () => {
      const label = currentWorkingState.label;
      if (isNew) return `We added the freeform topic '${label}' for you.`;
      if (item.deactivated !== currentWorkingState.deactivated) {
        return currentWorkingState.deactivated
          ? `'${label}' has been archived. Your freeform topics list just got a little lighter.`
          : `'${label}' is now active.`;
      }
      return `'${label}' has been updated.`;
    };

    const customToastMsg = getAddedorUpdatedMessage();

    isNew
      ? dispatch(add({}, { id: generateId(), ...currentWorkingState }, { customToastMsg }))
      : dispatch(
          update(
            { id: item.id },
            { ...currentWorkingState, _version: currentWorkingState._version + 1 },
            { customToastMsg }
          )
        );
  };
}

export default CustomAttributesEditor;
